import React, { cloneElement, Component, isValidElement } from 'react';
import classNames from 'classnames';
import omit from 'lodash/omit';
import debounce from 'lodash/debounce';
import { global } from 'choerodon-ui/shared';
import Animate from '../animate';
import Progress from '../progress/progress';
import { Size } from '../_util/enum';
import { ProgressType } from '../progress/enum';
import ConfigContext from '../config-provider/ConfigContext';
function renderIndicator(props, prefixCls) {
    const { indicator, size } = props;
    const dotClassName = `${prefixCls}-dot`;
    if (isValidElement(indicator)) {
        return cloneElement(indicator, {
            className: classNames(indicator.props.className, dotClassName),
        });
    }
    const defaultIndicator = global.DEFAULT_SPIN_INDICATOR;
    if (isValidElement(defaultIndicator)) {
        return cloneElement(defaultIndicator, {
            className: classNames(defaultIndicator.props.className, dotClassName),
        });
    }
    return (React.createElement(Progress, { size: size, className: dotClassName, type: ProgressType.loading }));
}
function shouldDelay(spinning, delay) {
    return !!spinning && !!delay && !isNaN(Number(delay));
}
export default class Spin extends Component {
    constructor(props, context) {
        super(props, context);
        this.debouncifyUpdateSpinning = (props) => {
            const { delay } = props || this.props;
            if (delay) {
                this.cancelExistingSpin();
                this.updateSpinning = debounce(this.originalUpdateSpinning, delay);
            }
        };
        this.updateSpinning = () => {
            const { spinning } = this.props;
            const { spinning: currentSpinning } = this.state;
            if (currentSpinning !== spinning) {
                this.setState({ spinning });
            }
        };
        const { spinning, delay } = props;
        const shouldBeDelayed = shouldDelay(spinning, delay);
        this.state = {
            spinning: spinning && !shouldBeDelayed,
        };
        this.originalUpdateSpinning = this.updateSpinning;
        this.debouncifyUpdateSpinning(props);
    }
    static get contextType() {
        return ConfigContext;
    }
    static setDefaultIndicator(indicator) {
        global.DEFAULT_SPIN_INDICATOR = indicator;
    }
    componentDidMount() {
        this.updateSpinning();
    }
    componentDidUpdate() {
        this.debouncifyUpdateSpinning();
        this.updateSpinning();
    }
    componentWillUnmount() {
        this.cancelExistingSpin();
    }
    cancelExistingSpin() {
        const { updateSpinning } = this;
        if (updateSpinning && updateSpinning.cancel) {
            updateSpinning.cancel();
        }
    }
    render() {
        const { className, size, prefixCls: customizePrefixCls, tip, wrapperClassName, children, style, ...restProps } = this.props;
        const { spinning } = this.state;
        const { getPrefixCls } = this.context;
        const prefixCls = getPrefixCls('spin', customizePrefixCls);
        const spinClassName = classNames(prefixCls, {
            [`${prefixCls}-sm`]: size === Size.small,
            [`${prefixCls}-lg`]: size === Size.large,
            [`${prefixCls}-spinning`]: spinning,
            [`${prefixCls}-show-text`]: !!tip,
        }, className);
        // fix https://fb.me/react-unknown-prop
        const divProps = omit(restProps, ['spinning', 'delay', 'indicator']);
        const spinElement = (React.createElement("div", Object.assign({}, divProps, { className: spinClassName, style: style, key: "loading" }),
            renderIndicator(this.props, prefixCls),
            tip ? React.createElement("div", { className: `${prefixCls}-text` }, tip) : null));
        if (children) {
            let animateClassName = `${prefixCls}-nested-loading`;
            if (wrapperClassName) {
                animateClassName += ` ${wrapperClassName}`;
            }
            const containerClassName = classNames({
                [`${prefixCls}-container`]: true,
                [`${prefixCls}-blur`]: spinning,
            });
            return (React.createElement(Animate, Object.assign({}, divProps, { component: "div", className: animateClassName, transitionName: "fade" }),
                spinning && spinElement,
                React.createElement("div", { className: containerClassName, key: "container" }, children)));
        }
        return spinElement;
    }
}
Spin.displayName = 'Spin';
Spin.defaultProps = {
    spinning: true,
    size: Size.default,
    wrapperClassName: '',
};
